import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Search, MapPin, Calendar, ArrowRight, Filter, X, AlertCircle, Building2, Map as MapIcon, ChevronDown } from "lucide-react";
import { SiteHeader, SiteFooter } from "../components/SiteLayout";
import SEO, { breadcrumb } from "../components/SEO";
import { normalizeBoeAddress, normalizeLocalidad } from "../utils/address";
import Countdown from "../components/Countdown";

const API = process.env.REACT_APP_BACKEND_URL;

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return iso; }
}

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

const STATIC_FAQ = [
  { q: "¿De dónde salen estas subastas?", a: "Todos los datos provienen del Portal de Subastas oficial del BOE (subastas.boe.es). Sincronizamos diariamente para que siempre tengas la información más reciente." },
  { q: "¿Cómo puedo participar en una subasta?", a: "Necesitas estar registrado en el Portal de Subastas del BOE con DNI electrónico o certificado digital. Antes de pujar, te recomendamos un análisis profesional de cargas, deudas y situación registral." },
  { q: "¿Por qué necesito ayuda profesional?", a: "En subastas BOE puedes adjudicarte un inmueble con cargas posteriores que sí siguen vigentes (IBI, comunidad, etc.), ocupación irregular, o errores en la valoración. Un informe previo evita sustos posteriores." },
];

export default function Subastas() {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState({ items: [], total: 0, total_pages: 1, provincias: {}, subtipos: {}, tipos: {} });
  const [stats, setStats] = useState(null);
  const [localidades, setLocalidades] = useState([]);
  const [loadingLocs, setLoadingLocs] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  const provincia = params.get("provincia") || "";
  const localidad = params.get("localidad") || "";
  const subtipo = params.get("subtipo") || "";
  const tipo = params.get("tipo") || "";
  const estado = params.get("estado") || "";
  const q = params.get("q") || "";
  const page = Number(params.get("page") || 1);

  useEffect(() => {
    setLoading(true);
    setError("");
    const qs = new URLSearchParams({ page: String(page), per_page: "24" });
    if (provincia) qs.set("provincia", provincia);
    if (localidad) qs.set("localidad", localidad);
    if (subtipo) qs.set("subtipo", subtipo);
    if (tipo) qs.set("tipo", tipo);
    if (estado) qs.set("estado", estado);
    if (q) qs.set("q", q);
    axios.get(`${API}/api/subastas?${qs.toString()}`)
      .then(r => setData(r.data))
      .catch(e => setError(e?.response?.data?.detail || "Error cargando subastas"))
      .finally(() => setLoading(false));
  }, [provincia, localidad, subtipo, tipo, estado, q, page]);

  useEffect(() => {
    axios.get(`${API}/api/subastas/stats`).then(r => setStats(r.data)).catch(() => {});
  }, []);

  // Carga dinámica de localidades cuando cambia la provincia
  useEffect(() => {
    if (!provincia) {
      setLocalidades([]);
      return;
    }
    setLoadingLocs(true);
    const qs = new URLSearchParams({ provincia });
    if (estado) qs.set("estado", estado);
    axios.get(`${API}/api/subastas/localidades?${qs.toString()}`)
      .then(r => setLocalidades(r.data.items || []))
      .catch(() => setLocalidades([]))
      .finally(() => setLoadingLocs(false));
  }, [provincia, estado]);

  const setFilter = (key, value) => {
    // IMPORTANTE: usar .toString() para garantizar compat con Safari iOS y
    // WebViews antiguos. `new URLSearchParams(URLSearchParams)` no copia los
    // valores en algunos navegadores → genera "limpiar todo" por error.
    const np = new URLSearchParams(params.toString());
    if (value) np.set(key, value); else np.delete(key);
    // Si cambia la provincia, resetear la localidad porque el dropdown depende de ella
    if (key === "provincia") np.delete("localidad");
    np.delete("page");
    setParams(np);
  };
  const clearFilters = () => setParams(new URLSearchParams());

  const activeFilters = useMemo(() => {
    const arr = [];
    if (estado) {
      const labels = { activa: "Activa", proxima: "Próxima", suspendida: "Suspendida temp.", terminada: "Terminada" };
      arr.push({ key: "estado", label: `Estado: ${labels[estado] || estado}` });
    }
    if (provincia) arr.push({ key: "provincia", label: `Provincia: ${data.provincias[provincia] || provincia}` });
    if (localidad) {
      const loc = localidades.find(l => l.value === localidad);
      arr.push({ key: "localidad", label: `Localidad: ${loc ? loc.label : localidad}` });
    }
    if (subtipo) arr.push({ key: "subtipo", label: `Tipo: ${data.subtipos[subtipo] || subtipo}` });
    if (tipo) arr.push({ key: "tipo", label: `Origen: ${data.tipos[tipo] || tipo}` });
    if (q) arr.push({ key: "q", label: `Búsqueda: ${q}` });
    return arr;
  }, [estado, provincia, localidad, subtipo, tipo, q, data.provincias, data.subtipos, data.tipos, localidades]);

  return (
    <div className="min-h-screen bg-[#1a110e] text-stone-200">
      <SEO
        title="Subastas BOE inmuebles · Buscador actualizado"
        description="Buscador actualizado de subastas inmobiliarias del BOE (judiciales, AEAT, notariales). Filtra por provincia, tipo de inmueble y fecha. Datos oficiales del Portal de Subastas. Sincronizado diariamente."
        path="/subastas"
        keywords="subastas BOE, subastas judiciales, subastas inmobiliarias, AEAT, buscador subastas, viviendas subasta"
        jsonLd={[breadcrumb([{ name: "Inicio", path: "/" }, { name: "Subastas BOE", path: "/subastas" }])]}
      />
      <SiteHeader />

      {/* HERO */}
      <section className="px-6 pt-12 pb-8 border-b border-stone-800">
        <div className="max-w-7xl mx-auto">
          <div className="text-[11px] tracking-[0.3em] uppercase font-bold text-[#E0651F] mb-3">
            <Building2 className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" /> Buscador de Subastas
          </div>
          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl text-white leading-[0.95] tracking-tight">
            Subastas <span className="text-[#E0651F]">BOE</span> de inmuebles.
            <br />
            <span className="text-stone-400 text-2xl sm:text-3xl lg:text-4xl font-normal">Datos oficiales, búsqueda fácil.</span>
          </h1>
          <p className="text-stone-400 mt-5 max-w-2xl text-sm sm:text-base leading-relaxed">
            Buscador en tiempo casi real del Portal de Subastas oficial del BOE. Filtra por provincia, tipo de inmueble y origen.
            {stats?.total_activas != null && (
              <> Ahora mismo <strong className="text-white">{stats.total_activas.toLocaleString("es-ES")}</strong> subastas activas.</>
            )}
            {stats?.ultima_actualizacion && (
              <span className="block text-xs text-stone-500 mt-2">
                Última actualización: {fmtDateTime(stats.ultima_actualizacion)} · Fuente: <a href="https://subastas.boe.es" target="_blank" rel="noopener noreferrer" className="underline hover:text-white">subastas.boe.es</a>
              </span>
            )}
          </p>
        </div>
      </section>

      <section className="px-6 py-8">
        <div className="max-w-7xl mx-auto flex gap-8 w-full">
          {/* SIDEBAR FILTERS (desktop) */}
          <aside className="hidden lg:block w-64 flex-shrink-0 space-y-6 sticky top-24 self-start">
            <FiltersBlock data={data} provincia={provincia} localidad={localidad} subtipo={subtipo} tipo={tipo} estado={estado} q={q} setFilter={setFilter} clearFilters={clearFilters} localidades={localidades} loadingLocs={loadingLocs} />
          </aside>

          {/* MAIN */}
          <div className="flex-1 min-w-0 w-full">
            {/* Mobile filter toggle */}
            <button
              className="lg:hidden mb-4 inline-flex items-center gap-2 bg-stone-800 hover:bg-stone-700 px-4 py-2 rounded-sm text-sm"
              onClick={() => setShowFilters(true)}
              data-testid="subastas-filters-mobile-open"
            >
              <Filter className="w-4 h-4" /> Filtros
              {activeFilters.length > 0 && <span className="bg-[#E0651F] text-white text-xs px-1.5 rounded-full">{activeFilters.length}</span>}
            </button>

            {/* Active filter chips */}
            {activeFilters.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4">
                {activeFilters.map(f => (
                  <button key={f.key} onClick={() => setFilter(f.key, "")} className="inline-flex items-center gap-1.5 bg-[#E0651F]/15 border border-[#E0651F]/40 text-[#E0651F] hover:bg-[#E0651F]/25 px-2.5 py-1 rounded-sm text-xs">
                    {f.label} <X className="w-3 h-3" />
                  </button>
                ))}
                <button onClick={clearFilters} className="text-xs text-stone-500 hover:text-white underline">Limpiar todo</button>
              </div>
            )}

            <div className="text-sm text-stone-500 mb-4" data-testid="subastas-results-count">
              {loading ? "Cargando…" : `${data.total.toLocaleString("es-ES")} resultados`}
            </div>

            {error && (
              <div className="bg-red-950/30 border border-red-900 text-red-300 p-4 rounded-sm mb-4 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 mt-0.5" /> {error}
              </div>
            )}

            {!loading && data.items.length === 0 && !error && (
              <div className="bg-stone-900 border border-stone-800 p-10 rounded-sm text-center">
                <p className="text-stone-400">No hay subastas con estos filtros.</p>
                <button onClick={clearFilters} className="mt-3 text-[#E0651F] hover:underline text-sm">Quitar filtros</button>
              </div>
            )}

            <div className="grid sm:grid-cols-2 gap-4" data-testid="subastas-grid">
              {data.items.map(s => <SubastaCard key={s.identificador} s={s} />)}
            </div>

            {/* Pagination */}
            {data.total_pages > 1 && (
              <Pagination page={page} totalPages={data.total_pages} onChange={(p) => {
                const np = new URLSearchParams(params);
                np.set("page", String(p));
                setParams(np);
                window.scrollTo({ top: 0, behavior: "smooth" });
              }} />
            )}
          </div>
        </div>
      </section>

      {/* Linking interno SEO: índice de provincias activas (las que tienen al
          menos 1 subasta). Cada link apunta a /subastas/{slug} (ProvinciaIndex). */}
      <ProvinciaLinksSection provincias={data.provincias} byProvincia={data.por_provincia} />

      {/* CTA */}
      <section className="px-6 py-16 border-t border-stone-800 bg-stone-950/50">
        <div className="max-w-4xl mx-auto text-center">
          <div className="text-[11px] tracking-[0.3em] uppercase font-bold text-[#E0651F] mb-3">¿Te interesa una subasta?</div>
          <h2 className="font-display font-black text-3xl sm:text-4xl text-white mb-4">
            No pujes a ciegas. Pide un informe profesional primero.
          </h2>
          <p className="text-stone-400 max-w-2xl mx-auto mb-8">
            Cargas posteriores, ocupación, deudas con comunidad o Hacienda… Un análisis previo te ahorra miles de euros y muchos disgustos.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/orientacion" className="bg-[#E0651F] hover:bg-white hover:text-[#1a110e] text-white px-6 py-3 text-xs tracking-[0.2em] uppercase font-bold rounded-sm transition-colors">
              Orientación 15 €
            </Link>
            <Link to="/consultoria" className="bg-transparent border border-stone-700 hover:border-white text-white px-6 py-3 text-xs tracking-[0.2em] uppercase font-bold rounded-sm transition-colors">
              Consultoría 95 €
            </Link>
            <Link to="/informe-premium" className="bg-transparent border border-stone-700 hover:border-white text-white px-6 py-3 text-xs tracking-[0.2em] uppercase font-bold rounded-sm transition-colors">
              Informe Premium
            </Link>
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="px-6 py-8 border-t border-stone-800">
        <div className="max-w-4xl mx-auto text-xs text-stone-500 leading-relaxed">
          <strong className="text-stone-400">Aviso:</strong> Los datos mostrados provienen del Portal de Subastas oficial del BOE
          (<a href="https://subastas.boe.es" target="_blank" rel="noopener noreferrer" className="underline hover:text-white">subastas.boe.es</a>),
          de conformidad con la Ley 37/2007 sobre reutilización de la información del sector público. Verifique siempre los datos en la fuente oficial
          antes de tomar decisiones. Sincronización diaria a las 08:00 (hora Madrid).
        </div>
      </section>

      <SiteFooter />

      {/* Mobile filters modal */}
      {showFilters && (
        <div className="lg:hidden fixed inset-0 z-50 bg-[#1a110e] overflow-y-auto" data-testid="subastas-filters-mobile">
          <div className="sticky top-0 bg-[#2d1f1a] px-6 py-4 flex justify-between items-center border-b border-stone-800">
            <h3 className="font-bold">Filtros</h3>
            <button onClick={() => setShowFilters(false)}><X className="w-5 h-5" /></button>
          </div>
          <div className="p-6">
            <FiltersBlock data={data} provincia={provincia} localidad={localidad} subtipo={subtipo} tipo={tipo} estado={estado} q={q} setFilter={setFilter} clearFilters={clearFilters} localidades={localidades} loadingLocs={loadingLocs} />
            <button onClick={() => setShowFilters(false)} className="mt-6 w-full bg-[#E0651F] text-white py-3 rounded-sm font-bold">Ver resultados</button>
          </div>
        </div>
      )}
    </div>
  );
}

function FiltersBlock({ data, provincia, localidad, subtipo, tipo, estado, q, setFilter, clearFilters, localidades, loadingLocs }) {
  const [qLocal, setQLocal] = useState(q);
  useEffect(() => setQLocal(q), [q]);
  const commitQ = () => {
    const v = qLocal.trim();
    if (v !== (q || "")) setFilter("q", v);
  };
  const submitQ = (e) => { e.preventDefault(); commitQ(); };
  return (
    <div className="space-y-6">
      <form onSubmit={submitQ}>
        <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-[#E0651F] mb-2">Búsqueda libre</div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-500" />
          <input
            type="search"
            value={qLocal}
            onChange={(e) => setQLocal(e.target.value)}
            onBlur={commitQ}
            placeholder="ID BOE, calle, descripción…"
            className="w-full bg-stone-900 border border-stone-800 rounded-sm pl-9 pr-3 py-2 text-sm placeholder:text-stone-600 focus:border-[#E0651F] focus:outline-none"
            data-testid="subastas-search-input"
            enterKeyHint="search"
          />
        </div>
        <div className="text-[10px] text-stone-500 mt-1.5 leading-snug">
          Busca por ID BOE (ej. <span className="font-mono">SUB-JA-2026-...</span>), por nombre de calle o palabra clave.
        </div>
      </form>

      <div>
        <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-[#E0651F] mb-2">Estado</div>
        <div className="space-y-1.5">
          <RadioOption label="Todas" value="" current={estado} onChange={(v) => setFilter("estado", v)} />
          <RadioOption label="🟢 Activas (celebrándose)" value="activa" current={estado} onChange={(v) => setFilter("estado", v)} />
          <RadioOption label="🟠 Activas · Suspendidas temp." value="suspendida" current={estado} onChange={(v) => setFilter("estado", v)} />
          <RadioOption label="🟡 Próximas (pendientes apertura)" value="proxima" current={estado} onChange={(v) => setFilter("estado", v)} />
          <RadioOption label="🔴 Terminadas" value="terminada" current={estado} onChange={(v) => setFilter("estado", v)} />
        </div>
      </div>

      <div>
        <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-[#E0651F] mb-2">Provincia</div>
        <div className="relative">
          <select value={provincia} onChange={(e) => setFilter("provincia", e.target.value)} className="w-full bg-stone-900 border border-stone-800 rounded-sm px-3 pr-9 py-2 text-sm focus:border-[#E0651F] focus:outline-none appearance-none cursor-pointer" data-testid="subastas-filter-provincia">
            <option value="">Todas las provincias</option>
            {Object.entries(data.provincias || {}).sort((a, b) => a[1].localeCompare(b[1])).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none text-[#E0651F]" />
        </div>
      </div>

      <div>
        <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-[#E0651F] mb-2">
          Localidad / Municipio
          {provincia && localidades.length > 0 && <span className="text-stone-500 normal-case tracking-normal font-normal ml-1">({localidades.length})</span>}
        </div>
        <div className="relative">
          <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-500 pointer-events-none z-10" />
          <select
            value={localidad}
            onChange={(e) => setFilter("localidad", e.target.value)}
            disabled={!provincia || loadingLocs}
            className="w-full bg-stone-900 border border-stone-800 rounded-sm pl-9 pr-9 py-2 text-sm focus:border-[#E0651F] focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed appearance-none cursor-pointer"
            data-testid="subastas-filter-localidad"
          >
            <option value="">
              {!provincia
                ? "Elige primero una provincia"
                : loadingLocs
                ? "Cargando localidades…"
                : localidades.length === 0
                ? "Sin localidades disponibles"
                : `Todas las localidades (${localidades.length})`}
            </option>
            {localidades.map(loc => (
              <option key={loc.value} value={loc.value}>{loc.label} ({loc.n})</option>
            ))}
          </select>
          <ChevronDown className={`absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none ${!provincia || loadingLocs ? "text-stone-700" : "text-[#E0651F]"}`} />
        </div>
        {!provincia && (
          <div className="text-[10px] text-stone-500 mt-1.5 leading-snug">
            Selecciona una provincia para activar el filtro de localidad.
          </div>
        )}
      </div>

      <div>
        <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-[#E0651F] mb-2">Tipo de inmueble</div>
        <div className="space-y-1.5">
          <RadioOption label="Todos" value="" current={subtipo} onChange={(v) => setFilter("subtipo", v)} />
          {Object.entries(data.subtipos || {}).map(([k, v]) => (
            <RadioOption key={k} label={v} value={k} current={subtipo} onChange={(val) => setFilter("subtipo", val)} />
          ))}
        </div>
      </div>

      <div>
        <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-[#E0651F] mb-2">Origen de la subasta</div>
        <div className="space-y-1.5">
          <RadioOption label="Todos" value="" current={tipo} onChange={(v) => setFilter("tipo", v)} />
          {Object.entries(data.tipos || {}).map(([k, v]) => (
            <RadioOption key={k} label={v} value={k} current={tipo} onChange={(val) => setFilter("tipo", val)} />
          ))}
        </div>
      </div>

      <button onClick={clearFilters} className="text-xs text-stone-500 hover:text-white underline">Limpiar todos los filtros</button>
    </div>
  );
}

function RadioOption({ label, value, current, onChange }) {
  const active = current === value;
  return (
    <button
      type="button"
      onClick={() => onChange(value)}
      className={`block w-full text-left px-2 py-1.5 text-sm rounded-sm transition-colors ${active ? "bg-[#E0651F]/15 text-[#E0651F] font-bold" : "text-stone-400 hover:text-white hover:bg-stone-900"}`}
      data-testid={`subastas-filter-opt-${value || "all"}`}
    >
      {label}
    </button>
  );
}

// Mapa inverso código → slug (para construir URLs /subastas/{slug}).
// Coincide con PROVINCIA_SLUG_TO_CODE en utils/provincias.js (slugs canónicos).
const CODE_TO_PROV_SLUG = {
  "01": "alava", "02": "albacete", "03": "alicante", "04": "almeria",
  "05": "avila", "06": "badajoz", "07": "islas-baleares", "08": "barcelona",
  "09": "burgos", "10": "caceres", "11": "cadiz", "12": "castellon",
  "13": "ciudad-real", "14": "cordoba", "15": "a-coruna", "16": "cuenca",
  "17": "girona", "18": "granada", "19": "guadalajara", "20": "gipuzkoa",
  "21": "huelva", "22": "huesca", "23": "jaen", "24": "leon",
  "25": "lleida", "26": "la-rioja", "27": "lugo", "28": "madrid",
  "29": "malaga", "30": "murcia", "31": "navarra", "32": "ourense",
  "33": "asturias", "34": "palencia", "35": "las-palmas", "36": "pontevedra",
  "37": "salamanca", "38": "santa-cruz-de-tenerife", "39": "cantabria",
  "40": "segovia", "41": "sevilla", "42": "soria", "43": "tarragona",
  "44": "teruel", "45": "toledo", "46": "valencia", "47": "valladolid",
  "48": "bizkaia", "49": "zamora", "50": "zaragoza", "51": "ceuta",
  "52": "melilla",
};

function ProvinciaLinksSection({ provincias }) {
  if (!provincias || Object.keys(provincias).length === 0) return null;
  // Ordenar alfabéticamente por nombre
  const sorted = Object.entries(provincias)
    .map(([code, name]) => ({ code, name, slug: CODE_TO_PROV_SLUG[code] }))
    .filter((p) => p.slug)
    .sort((a, b) => a.name.localeCompare(b.name, "es"));
  return (
    <section className="px-6 py-12 border-t border-stone-800 bg-stone-950/30" data-testid="provincia-links">
      <div className="max-w-6xl mx-auto">
        <h2 className="text-[11px] tracking-[0.3em] uppercase font-bold text-[#E0651F] mb-2">
          Subastas BOE por provincia
        </h2>
        <p className="text-stone-400 text-sm mb-6 max-w-3xl">
          Accede al listado actualizado de subastas inmobiliarias del BOE en cada provincia
          española. Datos oficiales de Hacienda, juzgados y otras administraciones.
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-x-4 gap-y-2 text-sm">
          {sorted.map((p) => (
            <Link
              key={p.code}
              to={`/subastas/${p.slug}`}
              className="text-stone-300 hover:text-[#E0651F] hover:underline truncate"
              data-testid={`provincia-link-${p.slug}`}
            >
              Subastas en {p.name}
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

export function SubastaCard({ s }) {
  const isTerm = s.status === "terminada";
  const isProxima = s.status === "proxima";
  const tipoLabel = (s.subtipo === "599" || !s.subtipo_nombre) ? "Inmueble" : s.subtipo_nombre;
  // Abreviar tipo de subasta para evitar overflow en cards mobile
  const TIPO_SHORT = {
    "Judicial": "Judicial",
    "Notarial": "Notarial",
    "AEAT": "AEAT",
    "Otras administraciones tributarias": "Admón.",
    "Administrativa": "Administrativa",
  };
  const tipoShort = TIPO_SHORT[s.tipo] || s.tipo || "";
  const valor = s.detalle?.datos_subasta?.valor_subasta || s.valor_subasta;
  const tipoCod = s.tipo_codigo || "";
  const pct = (tipoCod === "A" || tipoCod === "R" || tipoCod === "G") ? 0.50 : 0.70;
  const adjDirecta = (typeof valor === "number" && valor > 0) ? valor * pct : null;
  // Dirección real para título (preferimos detalle.bien.direccion del BOE)
  const bien = s.detalle?.bien || {};
  const direccionRaw = bien.direccion || s.direccion_corta || "";
  const direccion = normalizeBoeAddress(direccionRaw);
  const localidad = normalizeLocalidad(bien.localidad || "");
  const cp = bien.codigo_postal || "";
  const titulo = direccion
    ? `${tipoLabel} en ${direccion}`
    : (s.descripcion || `${tipoLabel} (sin dirección)`);
  // Construir URL Google Maps con la mejor precisión disponible:
  //  - Si Catastro nos dio coords → lat,lng exactas
  //  - Si no → dirección + localidad + provincia
  //  - Si tampoco hay dirección → solo localidad + provincia
  const coords = bien.coords;
  const hasCoords = coords && typeof coords.lat === "number" && typeof coords.lng === "number";
  const mapsUrl = hasCoords
    ? `https://www.google.com/maps?q=${coords.lat},${coords.lng}&z=18`
    : (() => {
        const q = [direccionRaw, bien.localidad || "", s.provincia, "España"].filter(Boolean).join(", ")
          || [bien.localidad || "", s.provincia, "España"].filter(Boolean).join(", ");
        return q ? `https://www.google.com/maps?q=${encodeURIComponent(q)}` : null;
      })();
  return (
    <Link
      to={s.url_seo || `/subastas/${s.slug || s.identificador}`}
      className={`block bg-stone-900 hover:bg-stone-800 border border-stone-800 hover:border-[#E0651F]/50 rounded-sm p-4 sm:p-5 transition-colors group overflow-hidden ${isTerm ? "opacity-75" : ""}`}
      data-testid={`subasta-card-${s.identificador}`}
    >
      {/* Row 1: tipo inmueble + status badge */}
      <div className="flex items-center justify-between gap-2 mb-2 min-w-0">
        <span className="text-[#E0651F] font-bold text-[10px] tracking-[0.2em] uppercase truncate flex-1 min-w-0">{tipoLabel}</span>
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Badge de urgencia en vivo: solo si la subasta está activa */}
          {!isTerm && (s.detalle?.datos_subasta?.fecha_fin || s.fecha_fin) && (
            <Countdown fechaFin={s.detalle?.datos_subasta?.fecha_fin || s.fecha_fin} variant="badge" />
          )}
          <StatusBadge status={s.status} />
        </div>
      </div>
      {/* Row 1b: BOE ID + indicador de lote */}
      <div className="text-[9px] tracking-[0.1em] text-stone-500 font-mono mb-2 truncate" data-testid="card-boe-id" title={`BOE ${s.identificador}`}>
        BOE · {s.parent_id || s.identificador}
        {s.lote_num ? (
          <span className="ml-1 inline-flex items-center px-1.5 py-[1px] rounded-sm bg-[#E0651F]/15 text-[#E0651F] font-bold">
            Lote {s.lote_num}/{s.total_lotes}
          </span>
        ) : null}
      </div>
      {/* Row 2: tipo subasta + provincia/localidad */}
      <div className="flex items-center gap-2 text-[10px] tracking-[0.15em] uppercase text-stone-500 mb-2 min-w-0">
        <span className="truncate">{tipoShort}</span>
        {(localidad || s.provincia) && (
          <>
            <span className="flex-shrink-0">·</span>
            <span className="truncate flex items-center gap-1 min-w-0">
              <MapPin className="w-3 h-3 flex-shrink-0" /> {localidad ? `${localidad}${s.provincia && localidad.toUpperCase() !== s.provincia.toUpperCase() ? `, ${s.provincia}` : ""}` : s.provincia}
            </span>
          </>
        )}
      </div>
      {/* Título descriptivo: dirección real si la conocemos, si no descripción */}
      <h3 className="text-white font-bold text-base sm:text-[15px] mb-2 line-clamp-3 leading-snug break-words" data-testid="card-titulo">
        {titulo}
      </h3>
      {/* Sub-info: CP + descripción registral resumida (cuando es distinta del título) */}
      {direccion && s.descripcion && s.descripcion.length < 200 && (
        <div className="text-[11px] text-stone-500 line-clamp-1 mb-2" title={s.descripcion}>
          {cp ? `CP ${cp} · ` : ""}{s.descripcion}
        </div>
      )}
      {adjDirecta != null && (
        <div className="mt-3 inline-flex items-baseline gap-1.5 bg-[#E0651F]/15 border border-[#E0651F]/30 px-2.5 py-1 rounded-sm max-w-full" data-testid="card-adjudicacion">
          <span className="text-[9px] tracking-[0.2em] uppercase text-stone-400 truncate">Adjudicación directa:</span>
          <span className="text-[#E0651F] font-bold text-xs whitespace-nowrap">{adjDirecta.toLocaleString("es-ES", { maximumFractionDigits: 0 })} €</span>
        </div>
      )}
      <div className="text-xs text-stone-500 mt-3 flex items-center gap-1.5 min-w-0">
        <Calendar className="w-3 h-3 flex-shrink-0" />
        <span className="truncate">{isTerm ? "Finalizó:" : (isProxima ? "Apertura:" : "Finaliza:")} <span className="text-stone-300">{fmtDate(s.fecha_fin)}</span></span>
      </div>
      <div className="mt-3 text-xs text-stone-600 truncate">{s.organismo}</div>
      <div className="mt-4 flex items-center justify-between gap-2">
        <span className="text-[10px] tracking-[0.25em] uppercase font-bold text-[#E0651F] group-hover:text-white inline-flex items-center gap-1">
          Ver detalle <ArrowRight className="w-3 h-3" />
        </span>
        {mapsUrl && (
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.open(mapsUrl, "_blank", "noopener,noreferrer"); }}
            className="text-[10px] tracking-[0.2em] uppercase font-bold text-stone-400 hover:text-[#E0651F] inline-flex items-center gap-1 px-2 py-1 border border-stone-800 hover:border-[#E0651F]/50 rounded-sm transition-colors"
            data-testid="card-map-btn"
            title="Ver ubicación aproximada en Google Maps"
          >
            <MapIcon className="w-3 h-3" /> Mapa
          </button>
        )}
      </div>
    </Link>
  );
}

function StatusBadge({ status, size = "sm" }) {
  const config = {
    terminada: { dot: "bg-red-500", glow: "shadow-[0_0_8px_rgba(239,68,68,0.6)]", text: "Terminada", textColor: "text-red-400", border: "border-red-900/50", pulse: false },
    proxima: { dot: "bg-amber-400", glow: "shadow-[0_0_8px_rgba(251,191,36,0.6)]", text: "Próxima", textColor: "text-amber-300", border: "border-amber-900/50", pulse: true },
    activa: { dot: "bg-emerald-500", glow: "shadow-[0_0_8px_rgba(16,185,129,0.6)]", text: "Activa", textColor: "text-emerald-400", border: "border-emerald-900/50", pulse: true },
    suspendida: { dot: "bg-orange-400", glow: "shadow-[0_0_8px_rgba(251,146,60,0.6)]", text: "Suspendida temp.", textColor: "text-orange-300", border: "border-orange-900/50", pulse: true },
  };
  const c = config[status] || config.activa;
  const padding = size === "lg" ? "px-3 py-1.5 text-xs" : "px-2 py-0.5 text-[10px]";
  return (
    <span
      className={`inline-flex items-center gap-1.5 ${padding} ${c.textColor} font-bold tracking-[0.15em] uppercase rounded-sm bg-stone-950/60 border ${c.border} flex-shrink-0`}
      data-testid={`subasta-status-${status}`}
    >
      <span className={`w-2 h-2 rounded-full ${c.dot} ${c.glow} ${c.pulse ? "animate-pulse" : ""}`} />
      {c.text}
    </span>
  );
}

function Pagination({ page, totalPages, onChange }) {
  const around = 2;
  const pages = [];
  for (let p = Math.max(1, page - around); p <= Math.min(totalPages, page + around); p++) pages.push(p);
  return (
    <div className="flex items-center justify-center gap-2 mt-10" data-testid="subastas-pagination">
      <button disabled={page <= 1} onClick={() => onChange(page - 1)} className="px-3 py-2 text-sm border border-stone-800 rounded-sm hover:border-stone-600 disabled:opacity-30">‹ Anterior</button>
      {pages[0] > 1 && <span className="text-stone-600">…</span>}
      {pages.map(p => (
        <button key={p} onClick={() => onChange(p)} className={`px-3 py-2 text-sm rounded-sm ${p === page ? "bg-[#E0651F] text-white" : "border border-stone-800 hover:border-stone-600"}`}>{p}</button>
      ))}
      {pages[pages.length - 1] < totalPages && <span className="text-stone-600">…</span>}
      <button disabled={page >= totalPages} onClick={() => onChange(page + 1)} className="px-3 py-2 text-sm border border-stone-800 rounded-sm hover:border-stone-600 disabled:opacity-30">Siguiente ›</button>
    </div>
  );
}
